import iconsList from '../../featuresData.json';

interface IIconsArray {
  id: number;
  title: string;
  icon: string;
  apiKey: string;
  type: string;
  typeFilters?: string;
  disabled?: boolean;
}

export function adaptPhotos(
  rawPhotos?: Array<number>,
  meta?: { photos_url: string; photos_res: string[] },
  productId?: number,
): { [resolution: string]: string[] } | undefined {
  if (!rawPhotos || !meta?.photos_url || !meta?.photos_res || productId == null) {
    return undefined;
  }

  const { photos_url: baseUrl, photos_res: resolutions } = meta;

  const adaptedPhotos: { [resolution: string]: string[] } = {};
  resolutions.forEach((res) => {
    adaptedPhotos[res] = [];
  });

  rawPhotos.forEach((photoId) => {
    resolutions.forEach((res) => {
      const url = `${baseUrl}${productId}-${photoId}-${res}.webp`;
      adaptedPhotos[res].push(url);
    });
  });
  return adaptedPhotos;
}

export const getIconsArray = (features: any, isEnabled = true) => {
  const featuresArray = Object.keys(features);

  const filteredIcon = featuresArray.filter((item) => features[item as keyof any] === isEnabled);

  const result = iconsList.filter((item) => filteredIcon.includes(item.apiKey));

  return result as Array<IIconsArray>;
};

export const disabledIcons = (feature: any) => {
  return getIconsArray(feature, false).map((featureDesbled) => {
    const res = Object.assign(featureDesbled, { disabled: true });
    return res;
  });
};

export const getIconsType = (features: any, type: string) => {
  const result = getIconsArray(features)
    .filter((item) => item.type === type)
    .map((item) => ({ icon: item.icon, text: item.title }));
  return result;
};

export const getFlagsIcons = (flags: Array<string>, type?: string) => {
  const result = iconsList.filter((item) => flags.includes(item.apiKey));

  return type
    ? result
        .filter((item) => item.type === type)
        .map((item) => ({ icon: item.icon, text: item.title }))
    : result;
};

export const getCountString = (counter: number, one: string, few: string, many: string): string => {
  const absCounter = Math.abs(counter);
  const n10 = absCounter % 10;
  const n100 = absCounter % 100;
  if (n10 === 1 && n100 !== 11) {
    return one;
  }
  if (n10 >= 2 && n10 <= 4 && !(n100 >= 12 && n100 <= 14)) {
    return few;
  }
  return many;
};

export const getSquare = (square: any) => {
  const result: { text: string }[] = [];
  if (square.bedKingSize && square.bedKingSize !== 0) {
    const bed2Text = getCountString(
      square.bedKingSize,
      'кровать King Size',
      'кровати King Size',
      'кроватей King Size',
    );
    result.push({ text: `${square.bedKingSize} ${bed2Text}` });
  }
  if (square.bed2 && square.bed2 !== 0) {
    const bed2Text = getCountString(
      square.bed2,
      'двухспальная кровать',
      'двухспальные кровати',
      'двухспальных кроватей',
    );
    result.push({ text: `${square.bed2} ${bed2Text}` });
  }
  if (square.bed1 && square.bed1 !== 0) {
    const bed1Text = getCountString(
      square.bed1,
      'односпальная кровать',
      'односпальные кровати',
      'односпальных кроватей',
    );
    result.push({ text: `${square.bed1} ${bed1Text}` });
  }
  if (square.sofa2 && square.sofa2 !== 0) {
    const sofa2Text = getCountString(
      square.sofa2,
      'двухспальный диван',
      'двухспальных дивана',
      'двухспальных диванов',
    );
    result.push({ text: `${square.sofa2} ${sofa2Text}` });
  }
  if (square.sofa1 && square.sofa1 !== 0) {
    const sofa1Text = getCountString(
      square.sofa1,
      'односпальный диван',
      'односпальных дивана',
      'односпальных диванов',
    );
    result.push({ text: `${square.sofa1} ${sofa1Text}` });
  }
  return result;
};

export const getRoomsType = (type: string, rooms?: number) => {
  if (rooms) {
    if (type === 'both' && rooms === 1) {
      return 'Изолированная комната + кухня';
    }
    if (type === 'both' && rooms === 2) {
      return 'Изолированная и проходная';
    }
    if (type === 'both' && rooms > 2) {
      return 'Одна комната проходная';
    }
    if (type === 'combined' && rooms === 1) {
      return 'Студия';
    }
  }
  if (type === 'both') {
    return 'Отдельные';
  }
  if (type === 'combined') {
    return 'Одна проходная';
  }
  if (type === 'separate') {
    return 'Отдельные';
  }
  return 'Изолированные';
};

export const toStringFilter = (filterParams: any) => {
  const stringFilter = JSON.stringify(filterParams);
  return stringFilter !== '{}' ? stringFilter : undefined;
};

export const formatedType = (rooms: number | undefined): string => {
  if (!rooms) {
    return '';
  }
  const types = [
    '', // Индекс 0 не используется, так как комнат минимум 1
    'Однокомнатная',
    'Двухкомнатная',
    'Трёхкомнатная',
    'Четырёхкомнатная',
    'Пятикомнатная',
  ];
  if (rooms >= 1 && rooms <= 5) {
    return types[rooms];
  }
  return `${rooms}-комнатная`;
};

export const getTypeCatalogItems = (type: number) => {
  switch (type) {
    case 4:
      return 'Подходящая квартира';
    case 3:
    case 2:
      return 'Частично подходящая квартира';
    case 1:
      return 'С переселением';
    case 0:
      return 'Подходит под базовые критерии';
    default:
      return 'Подходящая квартира';
  }
};

export const getSortSelected = (type: string) => {
  switch (type) {
    case 'Ближе к центру':
      return ['distance', 'ASC'];
    case 'Дороже':
      return ['price', 'DESC'];
    case 'Дешевле':
      return ['price', 'ASC'];
    case 'Популярные':
      return ['popular', 'DESC'];
    default:
      return ['distance', 'ASC'];
  }
};

export const declinationWord = (dateValue: number): string => {
  if (dateValue % 10 === 1 && dateValue % 100 !== 11) {
    return 'ночь';
  }
  if (
    dateValue % 10 >= 2 &&
    dateValue % 10 <= 4 &&
    (dateValue % 100 < 10 || dateValue % 100 >= 20)
  ) {
    return 'ночи';
  }
  return 'ночей';
};
